<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('vip.statisticsTitle') }}</span>
          <el-dropdown @command="handleExportCommand" trigger="click">
            <el-button type="success">
              <el-icon><Download /></el-icon>
              {{ $t('common.export') }}
              <el-icon class="el-icon--right"><ArrowDown /></el-icon>
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="xlsx">
                  <el-icon><Document /></el-icon>
                  {{ $t('common.exportExcel') }}
                </el-dropdown-item>
                <el-dropdown-item command="csv">
                  <el-icon><Tickets /></el-icon>
                  {{ $t('common.exportCSV') }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </template>

      <!-- Filter Bar -->
      <div class="filter-bar">
        <div class="filter-content">
          <QuickDateRangePicker
            v-model="dateRange"
            @change="handleFilterChange"
          />
          
          <el-select
            v-model="filters.customerId"
            :placeholder="$t('vip.selectCustomer')"
            clearable
            filterable
            style="width: 200px"
            @change="handleFilterChange"
          >
            <el-option
              v-for="customer in customers"
              :key="customer.id"
              :label="getCustomerName(customer)"
              :value="customer.id"
            />
          </el-select>
          
          <el-select
            v-model="filters.packageTypeId"
            :placeholder="$t('vip.selectPackageType')"
            clearable
            style="width: 180px"
            @change="handleFilterChange"
          >
            <el-option
              v-for="type in packageTypes"
              :key="type.id"
              :label="getTypeName(type)"
              :value="type.id"
            />
          </el-select>
          
          <el-select
            v-model="filters.vipPackageId"
            :placeholder="$t('vip.selectVipPackage')"
            clearable
            filterable
            style="width: 200px"
            @change="handleFilterChange"
          >
            <el-option
              v-for="pkg in vipPackages"
              :key="pkg.id"
              :label="getPackageName(pkg)"
              :value="pkg.id"
            />
          </el-select>
          
          <el-button type="primary" @click="resetFilters">
            <el-icon><Refresh /></el-icon>
            {{ $t('common.reset') }}
          </el-button>
        </div>
      </div>

      <!-- Statistics Cards -->
      <div class="stats-cards" v-loading="vipStore.loading">
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12" :md="6">
            <el-card class="stat-card" shadow="hover">
              <div class="stat-item">
                <div class="stat-icon income">
                  <el-icon><Money /></el-icon>
                </div>
                <div class="stat-content">
                  <div class="stat-label">{{ $t('vip.totalIncome') }}</div>
                  <div class="stat-value amount">¥{{ calculateTotalRevenue() }}</div>
                </div>
              </div>
            </el-card>
          </el-col>
          
          <el-col :xs="24" :sm="12" :md="6">
            <el-card class="stat-card" shadow="hover">
              <div class="stat-item">
                <div class="stat-icon packages">
                  <el-icon><Document /></el-icon>
                </div>
                <div class="stat-content">
                  <div class="stat-label">{{ $t('vip.totalPackagesSold') }}</div>
                  <div class="stat-value">{{ vipStore.vipStatistics?.totalCount || 0 }}</div>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>

      <!-- VIP Purchases Table -->
      <div class="table-section" v-if="vipStore.vipStatistics?.items?.length">
        <div class="section-header">
          <h3 class="section-title">{{ $t('vip.purchaseRecords') }}</h3>
          <el-button-group>
            <el-button :type="viewMode === 'list' ? 'primary' : 'default'" @click="viewMode = 'list'">
              <el-icon><List /></el-icon>
            </el-button>
            <el-button :type="viewMode === 'card' ? 'primary' : 'default'" @click="viewMode = 'card'">
              <el-icon><Grid /></el-icon>
            </el-button>
          </el-button-group>
        </div>
        
        <!-- List View -->
        <el-table 
          v-if="viewMode === 'list'"
          :data="vipStore.vipStatistics.items" 
          stripe
          style="width: 100%"
          :cell-style="{ padding: '12px 0' }"
        >
          <el-table-column prop="purchaseDate" :label="$t('vip.purchaseDate')" width="180">
            <template #default="{ row }">
              {{ formatDateTime(row.startTime) }}
            </template>
          </el-table-column>
          <el-table-column prop="customerName" :label="$t('vip.customer')" min-width="150" show-overflow-tooltip>
            <template #default="{ row }">
              {{ getCustomerDisplayName(row.customerName, row.customerId) }}
            </template>
          </el-table-column>
          <el-table-column prop="packageName" :label="$t('vip.packageName')" min-width="200" show-overflow-tooltip>
            <template #default="{ row }">
              {{ getPackageNameFromData(row) }}
            </template>
          </el-table-column>
          <el-table-column prop="packageTypeName" :label="$t('vip.packageType')" width="150" show-overflow-tooltip>
            <template #default="{ row }">
              {{ getTypeNameFromData(row) }}
            </template>
          </el-table-column>
          <el-table-column prop="paidAmount" :label="$t('vip.amount')" width="120" align="right">
            <template #default="{ row }">
              <span class="amount-text">¥{{ row.paidAmount?.toFixed(2) || '0.00' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="packageCourseHours" :label="$t('vip.courseHours')" width="110" align="center">
            <template #default="{ row }">
              <el-tag type="success" size="small">{{ row.packageCourseHours }}h</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="remainingHours" :label="$t('vip.remainingHours')" width="130" align="center">
            <template #default="{ row }">
              <el-tag :type="row.remainingHours > 0 ? 'warning' : 'info'" size="small">
                {{ row.remainingHours }}/{{ row.totalHours }}h
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="isActive" :label="$t('vip.status')" width="100" align="center">
            <template #default="{ row }">
              <el-tag :type="row.isActive ? 'success' : 'info'" size="small">
                {{ row.isActive ? $t('vip.active') : $t('vip.inactive') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column :label="$t('common.actions')" width="140" align="center" fixed="right">
            <template #default="{ row }">
              <el-button-group>
                <el-button type="primary" @click="openEditDialog(row)">
                  <el-icon><Edit /></el-icon>
                </el-button>
                <el-button type="warning" @click="openAdjustDialog(row)">
                  <el-icon><Timer /></el-icon>
                </el-button>
              </el-button-group>
            </template>
          </el-table-column>
        </el-table>

        <!-- Card View -->
        <div v-else class="card-view">
          <el-row :gutter="16">
            <el-col 
              v-for="row in vipStore.vipStatistics.items" 
              :key="row.customerVipId || row.id"
              :xs="24" :sm="12" :md="8" :lg="6"
            >
              <el-card class="vip-card" shadow="hover">
                <div class="vip-card-header">
                  <div class="customer-info">
                    <div class="customer-name">{{ getCustomerDisplayName(row.customerName, row.customerId) }}</div>
                    <div class="package-name">{{ getPackageNameFromData(row) }}</div>
                  </div>
                  <el-tag :type="row.isActive ? 'success' : 'info'" size="small">
                    {{ row.isActive ? $t('vip.active') : $t('vip.inactive') }}
                  </el-tag>
                </div>
                <div class="vip-card-body">
                  <div class="vip-card-row">
                    <span class="label">{{ $t('vip.purchaseDate') }}:</span>
                    <span class="value">{{ formatDateTime(row.startTime) }}</span>
                  </div>
                  <div class="vip-card-row">
                    <span class="label">{{ $t('vip.packageType') }}:</span>
                    <span class="value">{{ getTypeNameFromData(row) }}</span>
                  </div>
                  <div class="vip-card-row">
                    <span class="label">{{ $t('vip.amount') }}:</span>
                    <span class="value amount-text">¥{{ row.paidAmount?.toFixed(2) || '0.00' }}</span>
                  </div>
                  <div class="vip-card-row">
                    <span class="label">{{ $t('vip.courseHours') }}:</span>
                    <el-tag type="success" size="small">{{ row.packageCourseHours }}h</el-tag>
                  </div>
                  <div class="vip-card-row">
                    <span class="label">{{ $t('vip.remainingHours') }}:</span>
                    <el-tag :type="row.remainingHours > 0 ? 'warning' : 'info'" size="small">
                      {{ row.remainingHours }}/{{ row.totalHours }}h
                    </el-tag>
                  </div>
                </div>
                <div class="vip-card-actions">
                  <el-button type="primary" @click="openEditDialog(row)">
                    <el-icon><Edit /></el-icon>
                    {{ $t('common.edit') }}
                  </el-button>
                  <el-button type="warning" @click="openAdjustDialog(row)">
                    <el-icon><Timer /></el-icon>
                    {{ $t('vip.adjustHours') }}
                  </el-button>
                </div>
              </el-card>
            </el-col>
          </el-row>
        </div>
        
        <!-- Pagination -->
        <div class="pagination-container" v-if="vipStore.vipStatistics.totalPages > 1">
          <el-pagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="vipStore.vipStatistics.totalCount"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
      </div>

      <el-empty 
        v-if="!vipStore.vipStatistics && !vipStore.loading" 
        :description="$t('vip.noStatisticsData')" 
      />
    </el-card>

    <!-- Edit Customer VIP Dialog -->
    <el-dialog
      v-model="showEditDialog"
      :title="$t('vip.editCustomerVip')"
      width="90%"
      :fullscreen="isMobile"
      destroy-on-close
      class="edit-vip-dialog"
    >
      <div v-loading="editLoading" class="edit-form-content">
        <el-form :model="editForm" :label-width="isMobile ? '90px' : '120px'" class="edit-form">
          <el-form-item :label="$t('vip.startTime')">
            <el-date-picker
              v-model="editForm.startTime"
              type="datetime"
              :placeholder="$t('vip.selectStartTime')"
              format="YYYY-MM-DD HH:mm"
              value-format="YYYY-MM-DD HH:mm"
              :shortcuts="dateShortcuts"
              style="width: 100%"
              popper-class="date-picker-custom"
            />
          </el-form-item>
          
          <el-form-item :label="$t('vip.endTime')">
            <el-date-picker
              v-model="editForm.endTime"
              type="datetime"
              :placeholder="$t('vip.selectEndTime')"
              format="YYYY-MM-DD HH:mm"
              value-format="YYYY-MM-DD HH:mm"
              :shortcuts="dateShortcuts"
              style="width: 100%"
              popper-class="date-picker-custom"
            />
          </el-form-item>
          
          <el-form-item :label="$t('vip.isActive')">
            <div class="switch-wrapper">
              <el-switch v-model="editForm.isActive" active-text="Active" inactive-text="Inactive" />
            </div>
          </el-form-item>
          
          <el-form-item :label="$t('vip.paidAmount')">
            <el-input-number
              v-model="editForm.paidAmount"
              :min="0"
              :precision="2"
              :step="1"
              controls-position="right"
              style="width: 100%"
              :placeholder="$t('vip.enterPaidAmount')"
            >
              <template #prepend>
                <span style="font-weight: 600; color: #67c23a">¥</span>
              </template>
            </el-input-number>
          </el-form-item>
          
          <el-form-item :label="$t('vip.changeReason')" required>
            <el-input 
              v-model="editForm.changeReason" 
              type="textarea"
              :placeholder="$t('vip.enterChangeReason')"
              :rows="isMobile ? 4 : 3"
              show-word-limit
              :maxlength="500"
            />
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeEditDialog" size="large" style="flex: 1">
            {{ $t('common.cancel') }}
          </el-button>
          <el-button type="primary" @click="saveEdit" :loading="editLoading" size="large" style="flex: 1">
            {{ $t('common.save') }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- Adjust Hours Dialog -->
    <el-dialog
      v-model="showAdjustDialog"
      :title="adjustType === 'increase' ? $t('vip.increaseHours') : $t('vip.decreaseHours')"
      width="90%"
      :fullscreen="isMobile"
      destroy-on-close
      class="adjust-hours-dialog"
    >
      <div v-loading="adjustLoading" class="adjust-form-content">
        <!-- Current Status Display -->
        <div class="current-status">
          <div class="status-card current">
            <div class="status-label">{{ $t('vip.currentHours') }}</div>
            <div class="status-value">{{ selectedVipPackage?.remainingHours || 0 }}</div>
          </div>
          <div class="status-arrow">
            <el-icon><Right /></el-icon>
          </div>
          <div class="status-card preview" :class="adjustType">
            <div class="status-label">{{ $t('vip.newHours') }}</div>
            <div class="status-value">{{ calculatePreviewHours() }}</div>
          </div>
        </div>

        <!-- Adjustment Type Toggle -->
        <div class="adjustment-toggle">
          <el-radio-group v-model="adjustType" size="large">
            <el-radio-button value="increase">
              <el-icon><Plus /></el-icon>
              {{ $t('vip.increaseHours') }}
            </el-radio-button>
            <el-radio-button value="decrease">
              <el-icon><Minus /></el-icon>
              {{ $t('vip.decreaseHours') }}
            </el-radio-button>
          </el-radio-group>
        </div>

        <!-- Custom Adjust Input -->
        <div class="custom-adjust-section">
          <div class="section-label">{{ $t('vip.customAdjust') }}</div>
          <div class="custom-input-wrapper">
            <el-input-number
              v-model="adjustForm.hours"
              :min="1"
              :max="adjustType === 'decrease' ? (selectedVipPackage?.remainingHours || 0) : 999"
              :precision="0"
              :step="1"
              size="large"
              style="width: 100%"
            />
            <div class="hours-preview">
              {{ adjustType === 'increase' ? '+' : '-' }}{{ adjustForm.hours || 0 }} {{ $t('vip.hoursChange') }}
            </div>
          </div>
        </div>

        <!-- Reason Input -->
        <div class="reason-section">
          <div class="section-label">{{ $t('vip.adjustmentReason') }} *</div>
          <el-select
            v-model="adjustForm.reasonType"
            :placeholder="$t('vip.selectReason')"
            style="width: 100%; margin-bottom: 12px"
          >
            <el-option :label="$t('vip.reasonGift')" value="gift" />
            <el-option :label="$t('vip.reasonCorrection')" value="correction" />
            <el-option :label="$t('vip.reasonRefund')" value="refund" />
            <el-option :label="$t('vip.reasonOther')" value="other" />
          </el-select>
          <el-input
            v-model="adjustForm.reason"
            type="textarea"
            :rows="3"
            :placeholder="$t('vip.enterAdjustmentReason')"
            show-word-limit
            :maxlength="500"
          />
        </div>

        <!-- Warning for Decrease -->
        <div v-if="adjustType === 'decrease'" class="warning-message">
          <el-icon><Warning /></el-icon>
          <span>{{ $t('vip.decreaseWarning') }}</span>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeAdjustDialog" size="large" style="flex: 1">
            {{ $t('common.cancel') }}
          </el-button>
          <el-button
            type="primary"
            @click="saveAdjustHours"
            :loading="adjustLoading"
            size="large"
            style="flex: 1"
            :disabled="!canSubmitAdjust"
          >
            {{ $t('common.confirm') }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Money, Document, Download, ArrowDown, Tickets, Refresh, Edit, Plus, Minus, Right, Warning, List, Grid, Timer } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useVipStore } from '@/stores/vip'
import QuickDateRangePicker from '@/components/QuickDateRangePicker.vue'
import { exportToExcel, exportToCSV } from '@/utils/export'
import dayjs from 'dayjs'
import MTime from '@/utils/time-handler'
import { getCustomerList } from '@/api/customer'
import { getVipPackages, getVipPackageTypes } from '@/api/vip'

const { t } = useI18n()
const vipStore = useVipStore()

// Check if mobile device
const isMobile = ref(window.innerWidth < 768)

// Handle window resize
const handleResize = () => {
  isMobile.value = window.innerWidth < 768
}

// Date picker shortcuts
const dateShortcuts = [
  {
    text: 'Today',
    value: () => {
      const date = new Date()
      return date
    }
  },
  {
    text: 'Tomorrow',
    value: () => {
      const date = new Date()
      date.setTime(date.getTime() + 3600 * 1000 * 24)
      return date
    }
  },
  {
    text: 'Next Week',
    value: () => {
      const date = new Date()
      date.setTime(date.getTime() + 3600 * 1000 * 24 * 7)
      return date
    }
  },
  {
    text: 'Next Month',
    value: () => {
      const date = new Date()
      date.setMonth(date.getMonth() + 1)
      return date
    }
  }
]

// Date filter - use array format for QuickDateRangePicker
const dateRange = ref([dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')])

// Additional filters
const filters = ref({
  customerId: null,
  packageTypeId: null,
  vipPackageId: null
})

// Pagination
const currentPage = ref(1)
const pageSize = ref(20)

// View mode toggle
const viewMode = ref('list') // 'list' | 'card'

// Dropdown data
const customers = ref([])
const vipPackages = ref([])
const packageTypes = ref([])

// Edit dialog state
const showEditDialog = ref(false)
const editLoading = ref(false)
const editForm = ref({
  customerVipId: null,
  startTime: null,
  endTime: null,
  isActive: null,
  paidAmount: null,
  changeReason: ''
})

const selectedVipPackage = ref(null)

// Adjust hours dialog
const showAdjustDialog = ref(false)
const adjustLoading = ref(false)
const adjustType = ref('increase') // 'increase' | 'decrease'

const adjustForm = ref({
  hours: null,
  reasonType: '',
  reason: ''
})

// Handle filter change with pagination reset
const handleFilterChange = () => {
  currentPage.value = 1
  fetchStatistics()
}

// Fetch statistics
const fetchStatistics = () => {
  const params = {
    pageIndex: currentPage.value,
    pageSize: pageSize.value
  }
  
  // Use dateRange array - QuickDateRangePicker now provides UTC ISO format via change event
  if (dateRange.value && dateRange.value.length === 2) {
    params.startDate = MTime.formatQueryStartDate(dateRange.value[0])
    params.endDate = MTime.formatQueryEndDate(dateRange.value[1])
  }
  
  // Add filter parameters
  if (filters.value.customerId) {
    params.customerId = filters.value.customerId
  }
  if (filters.value.packageTypeId) {
    params.packageTypeId = filters.value.packageTypeId
  }
  if (filters.value.vipPackageId) {
    params.vipPackageId = filters.value.vipPackageId
  }
  
  vipStore.fetchVipStatistics(params)
}

// Format date time
const formatDateTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Export handlers
const handleExportCommand = (format) => {
  if (format === 'csv') {
    handleExport('csv')
  } else {
    handleExport('xlsx')
  }
}

const handleExport = async (format = 'xlsx') => {
  try {
    const stats = vipStore.vipStatistics
    
    if (!stats || !stats.items || stats.items.length === 0) {
      ElMessage.warning(t('common.noData'))
      return
    }
    
    // Prepare purchase records data
    const recordsData = stats.items.map(item => ({
      'Purchase Date': formatDateTime(item.startTime),
      'Customer': getCustomerDisplayName(item.customerName, item.customerId),
      'Package Name': getPackageNameFromData(item),
      'Package Type': getTypeNameFromData(item),
      'Amount': `¥${item.paidAmount?.toFixed(2) || '0.00'}`,
      'Course Hours': `${item.packageCourseHours || 0}h`,
      'Remaining Hours': `${item.remainingHours || 0}/${item.totalHours || 0}h`,
      'Status': item.isActive ? t('vip.active') : t('vip.inactive'),
      'Out Trade No': item.outTradeNo || ''
    }))
    
    // Export based on format
    if (format === 'csv') {
      exportToCSV(recordsData, 'vip-purchase-records')
    } else {
      // For Excel, create single sheet with all records
      const XLSX = await import('xlsx')
      const wb = XLSX.utils.book_new()
      
      const wsRecords = XLSX.utils.json_to_sheet(recordsData)
      XLSX.utils.book_append_sheet(wb, wsRecords, 'Purchase Records')
      
      // Auto-size columns
      wsRecords['!cols'] = [
        { wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 20 }, 
        { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 12 }, { wch: 30 }
      ]
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19).replace('T', '_')
      XLSX.writeFile(wb, `vip-purchase-records_${timestamp}.xlsx`)
    }
  } catch (error) {
    console.error('Export failed:', error)
    ElMessage.error(error.message || t('common.exportFailed'))
  }
}

// Helper functions to get display names
const getCustomerName = (customer) => {
  if (!customer) return ''
  return customer.name || customer.customerName || customer.Name || customer.CustomerName || `Customer ${customer.id}`
}

const getTypeName = (type) => {
  if (!type) return ''
  // Try different possible field names for type name
  return type.typeName || type.name || type.TypeName || type.Name || `Type ${type.id}`
}

const getPackageName = (pkg) => {
  if (!pkg) return ''
  // Try different possible field names for package name
  return pkg.packageName || pkg.name || pkg.PackageName || pkg.Name || `Package ${pkg.id}`
}

// Get display names from API response data
const getCustomerDisplayName = (customerName, customerId) => {
  if (customerName && customerName !== 'Unknown') {
    return customerName
  }
  // Try to find customer name from dropdown
  const customer = customers.value.find(c => c.id === customerId)
  return customer ? getCustomerName(customer) : (customerName || `Customer ${customerId}`)
}

const getPackageNameFromData = (row) => {
  return row.packageName || `Package ${row.vipPackageId}`
}

const getTypeNameFromData = (row) => {
  if (row.packageTypeName) {
    return row.packageTypeName
  }
  // Try to find from package types dropdown
  const type = packageTypes.value.find(t => t.id === row.packageTypeId)
  return type ? getTypeName(type) : (row.packageTypeId ? `Type ${row.packageTypeId}` : '-')
}

// Use API-provided aggregated totals
const calculateTotalRevenue = () => {
  const stats = vipStore.vipStatistics
  if (!stats) return '0.00'
  
  // Try different possible field names for total paid amount
  const total = stats.totalPaidAmount || stats.totalIncome || stats.totalRevenue || 0
  return total.toFixed(2)
}

// Pagination handlers
const handleSizeChange = (val) => {
  pageSize.value = val
  currentPage.value = 1
  fetchStatistics()
}

const handlePageChange = (val) => {
  currentPage.value = val
  fetchStatistics()
}

// Reset filters
const resetFilters = () => {
  filters.value = {
    customerId: null,
    packageTypeId: null,
    vipPackageId: null
  }
  dateRange.value = [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]
  currentPage.value = 1
  fetchStatistics()
}

// Load dropdown data
const loadDropdownData = async () => {
  try {
    // Load customers
    const customerRes = await getCustomerList({ pageSize: 1000 })
    if (customerRes.success) {
      const data = customerRes.data
      customers.value = Array.isArray(data) ? data : (data.items || data.list || [])
    }
    
    // Load VIP packages
    const packageRes = await getVipPackages({ activeOnly: true })
    if (packageRes.success) {
      vipPackages.value = packageRes.data || []
    }
    
    // Load package types
    const typeRes = await getVipPackageTypes({ isActive: true })
    if (typeRes.success) {
      const data = typeRes.data
      packageTypes.value = Array.isArray(data) ? data : (data.items || data.list || data.packageTypes || [])
    }
  } catch (error) {
    console.error('Failed to load dropdown data:', error)
  }
}

// Edit dialog functions
const openEditDialog = (row) => {
  editForm.value = {
    customerVipId: row.customerVipId || row.id || null,
    startTime: row.startTime ? dayjs(row.startTime).format('YYYY-MM-DD HH:mm') : null,
    endTime: row.endTime ? dayjs(row.endTime).format('YYYY-MM-DD HH:mm') : null,
    isActive: row.isActive !== undefined ? row.isActive : null,
    paidAmount: row.paidAmount || null,
    changeReason: ''
  }
  showEditDialog.value = true
}

const closeEditDialog = () => {
  showEditDialog.value = false
  editForm.value = {
    customerVipId: null,
    startTime: null,
    endTime: null,
    isActive: null,
    paidAmount: null,
    changeReason: ''
  }
}

const saveEdit = async () => {
  if (!editForm.value.customerVipId) {
    ElMessage.error(t('common.invalidData'))
    return
  }
  
  if (!editForm.value.changeReason.trim()) {
    ElMessage.warning(t('vip.changeReasonRequired'))
    return
  }
  
  editLoading.value = true
  
  try {
    const data = {
      customerVipId: editForm.value.customerVipId,
      startTime: editForm.value.startTime ? dayjs(editForm.value.startTime).toISOString() : null,
      endTime: editForm.value.endTime ? dayjs(editForm.value.endTime).toISOString() : null,
      isActive: editForm.value.isActive,
      paidAmount: editForm.value.paidAmount,
      changeReason: editForm.value.changeReason.trim()
    }
    
    const res = await vipStore.updateCustomerVipPackage(editForm.value.customerVipId, data)
    
    if (res.success) {
      ElMessage.success(t('common.updateSuccess'))
      closeEditDialog()
      fetchStatistics()
    } else {
      ElMessage.error(res.message || t('common.updateFailed'))
    }
  } catch (error) {
    console.error('Update customer VIP failed:', error)
    ElMessage.error(t('common.updateFailed'))
  } finally {
    editLoading.value = false
  }
}

// Adjust hours dialog functions
const openAdjustDialog = (row) => {
  selectedVipPackage.value = row
  adjustForm.value = {
    hours: null,
    reasonType: '',
    reason: ''
  }
  adjustType.value = 'increase'
  showAdjustDialog.value = true
}

// Calculate preview hours
const calculatePreviewHours = () => {
  if (!selectedVipPackage.value) return 0
  const current = selectedVipPackage.value.remainingHours || 0
  const adjustment = adjustForm.value.hours || 0
  return adjustType.value === 'increase'
    ? current + adjustment
    : Math.max(0, current - adjustment)
}

// Check if can submit
const canSubmitAdjust = computed(() => {
  return adjustForm.value.hours > 0 &&
         (adjustForm.value.reasonType || adjustForm.value.reason)
})

// Close dialog
const closeAdjustDialog = () => {
  showAdjustDialog.value = false
  adjustForm.value = { hours: null, reasonType: '', reason: '' }
}

// Save adjustments
const saveAdjustHours = async () => {
  if (!canSubmitAdjust.value) {
    ElMessage.warning(t('vip.adjustFailed'))
    return
  }

  const customerVipId = selectedVipPackage.value.customerVipId || selectedVipPackage.value.id
  if (!customerVipId) {
    ElMessage.error(t('common.invalidData'))
    return
  }

  adjustLoading.value = true
  try {
    const hoursAdjustment = adjustType.value === 'increase'
      ? adjustForm.value.hours
      : -adjustForm.value.hours

    const adjustmentReason = adjustForm.value.reasonType
      ? `[${adjustForm.value.reasonType}] ${adjustForm.value.reason}`
      : adjustForm.value.reason

    const res = await vipStore.adjustCustomerVipHours(customerVipId, {
      customerVipId: customerVipId,
      hoursAdjustment: hoursAdjustment,
      adjustmentReason: adjustmentReason
    })

    if (res.success) {
      ElMessage.success(t('vip.adjustSuccess'))
      closeAdjustDialog()
      fetchStatistics()
    } else {
      ElMessage.error(res.message || t('vip.adjustFailed'))
    }
  } catch (error) {
    console.error('Adjust hours error:', error)
    ElMessage.error(t('vip.adjustFailed'))
  } finally {
    adjustLoading.value = false
  }
}

onMounted(() => {
  loadDropdownData()
  fetchStatistics()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.page-container {
  padding: 12px;
  background-color: var(--bg-color-page, #f5f7fa);
  transition: background-color 0.3s ease;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.card-header span {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary, #303133);
  transition: color 0.3s ease;
}

.filter-bar {
  margin-bottom: 16px;
  padding: 12px;
  background: var(--bg-color-white, #fff);
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: background-color 0.3s ease;
}

.filter-content {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.filter-content > * {
  flex-shrink: 0;
}

.stats-cards {
  margin-bottom: 20px;
}

.stat-card {
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  flex-shrink: 0;
}

/* Edit Dialog Styles */
.edit-form-content {
  padding: 12px 0;
}

.edit-form {
  max-width: 100%;
}

.switch-wrapper {
  display: flex;
  align-items: center;
  padding: 8px 0;
}

.dialog-footer {
  display: flex;
  gap: 12px;
  width: 100%;
}

/* Custom date picker styles */
:deep(.date-picker-custom) {
  z-index: 9999;
}

:deep(.el-input-number) {
  width: 100%;
}

:deep(.el-input-number .el-input__inner) {
  padding-left: 40px;
  font-weight: 500;
  font-size: 15px;
}

:deep(.el-date-picker) {
  width: 100%;
}

:deep(.el-date-picker .el-input__inner) {
  font-weight: 500;
  font-size: 15px;
}

:deep(.el-form-item__label) {
  font-weight: 500;
  color: #606266;
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .edit-form-content {
    padding: 8px 0;
  }

  .dialog-footer {
    gap: 8px;
  }
}

/* Adjust Hours Dialog Styles */
.adjust-form-content {
  padding: 16px 0;
}

.current-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 8px;
}

.status-card {
  flex: 1;
  text-align: center;
  padding: 16px;
  border-radius: 8px;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.status-card.current .status-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
}

.status-card.current .status-value {
  font-size: 24px;
  font-weight: 600;
  color: #409EFF;
}

.status-card.preview.increase .status-value {
  color: #67C23A;
}

.status-card.preview.decrease .status-value {
  color: #F56C6C;
}

.status-arrow {
  color: #909399;
  font-size: 20px;
}

.adjustment-toggle {
  text-align: center;
  margin-bottom: 24px;
}

.adjustment-toggle .el-radio-button__inner {
  padding: 12px 24px;
  font-size: 16px;
}

.quick-adjust-section,
.custom-adjust-section,
.reason-section {
  margin-bottom: 24px;
}

.section-label {
  font-size: 14px;
  font-weight: 500;
  color: #606266;
  margin-bottom: 12px;
}

.quick-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.quick-buttons .el-button {
  flex: 1;
  min-width: 60px;
}

.custom-input-wrapper {
  position: relative;
}

.hours-preview {
  text-align: center;
  margin-top: 8px;
  font-size: 14px;
  color: #909399;
}

.warning-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #fef0f0;
  border: 1px solid #fde2e2;
  border-radius: 4px;
  color: #F56C6C;
  font-size: 14px;
}

.stat-icon.income {
  background: linear-gradient(135deg, #67c23a 0%, #95d475 100%);
  color: white;
}

.stat-icon.packages {
  background: linear-gradient(135deg, #409eff 0%, #79bbff 100%);
  color: white;
}

.stat-icon.average {
  background: linear-gradient(135deg, #e6a23c 0%, #eebe77 100%);
  color: white;
}

.stat-icon.hours {
  background: linear-gradient(135deg, #909399 0%, #c0c4cc 100%);
  color: white;
}

.stat-icon.customers {
  background: linear-gradient(135deg, #f56c6c 0%, #f89898 100%);
  color: white;
}

.stat-icon.popular {
  background: linear-gradient(135deg, #722ed1 0%, #9254de 100%);
  color: white;
}

.stat-value.package-name {
  font-size: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 13px;
  color: var(--color-text-regular, #606266);
  margin-bottom: 8px;
  transition: color 0.3s ease;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-primary, #303133);
  transition: color 0.3s ease;
}

.stat-value.amount {
  color: var(--color-primary, var(--el-color-primary));
}

.breakdown-section,
.chart-section,
.transactions-section,
.table-section {
  margin-top: 24px;
}

.pagination-container {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--border-color-light, #e4e7ed);
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-primary, #303133);
  margin: 0;
  padding: 0;
  border: none;
  transition: color 0.3s ease;
}

.amount-text {
  font-weight: 600;
  color: var(--color-primary, var(--el-color-primary));
  font-size: 14px;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .page-container {
    padding: 8px;
  }
  
  .card-header span {
    font-size: 15px;
  }
  
  .filter-bar {
    padding: 10px;
  }
  
  .filter-content {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-content > * {
    width: 100% !important;
  }
  
  .stat-icon {
    width: 50px;
    height: 50px;
    font-size: 24px;
  }
  
  .stat-value {
    font-size: 20px;
  }

  .section-header {
    margin-bottom: 10px;
    padding-bottom: 6px;
  }

  .section-title {
    font-size: 14px;
  }
}

/* Small mobile devices */
@media (max-width: 480px) {
  .stat-label {
    font-size: 12px;
  }
  
  .stat-value {
    font-size: 18px;
  }
}

/* Card View Styles */
.card-view {
  margin-top: 16px;
}

.vip-card {
  margin-bottom: 16px;
  border-radius: 8px;
  transition: all 0.3s;
}

.vip-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.vip-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-color-light, #e4e7ed);
}

.customer-info {
  flex: 1;
  min-width: 0;
}

.customer-name {
  font-weight: 600;
  font-size: 15px;
  color: var(--color-text-primary, #303133);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.package-name {
  font-size: 13px;
  color: var(--color-text-regular, #606266);
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vip-card-body {
  margin-bottom: 12px;
}

.vip-card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 13px;
}

.vip-card-row .label {
  color: var(--color-text-regular, #606266);
}

.vip-card-row .value {
  color: var(--color-text-primary, #303133);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 120px;
}

.vip-card-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid var(--border-color-light, #e4e7ed);
}

.vip-card-actions .el-button {
  flex: 1;
}

@media (max-width: 768px) {
  .vip-card-actions {
    flex-direction: column;
  }
  
  .vip-card-actions .el-button {
    width: 100%;
  }
}
</style>
